import { uniqBy } from 'lodash';
import { stringify } from 'query-string';
import type { ExportMetaData } from 'api/export/types';
import { HttpClient } from 'api/http-client';
import {
  Company,
  PermissionSet,
  Team,
  User,
  UserStatus,
  UserType,
} from 'api/models';
import { QueryCombinerType } from 'api/tasks/filter-types';
import type { GroupCriteria, FieldFilter } from 'api/tasks/filter-types';
import {
  AssigneePickerFilter,
  AssigneePickerFilterType,
  AssigneePickerObjectTypes,
} from 'hooks/assignee-picker/types/types';
import type { ProjectPermissionSet } from 'stores/permission-sets';
import { UserOption } from 'utils/project-fields/types';
import { getSimpleSearchFilterModule } from 'utils/simple-search-filter/module';
import type { Member, MembersResponse } from './types';

interface ProjectMember {
  user: Pick<User, 'userId'>;
  permissionSet: Pick<ProjectPermissionSet, 'permissionSetType'>;
  joinedAt?: number;
  status?: boolean;
}

export interface InvitedUser extends Omit<Member, 'joinedAt' | 'active'> {
  status?: boolean;
}

export interface ProjectMembership {
  projectId: number;
  members: ProjectMember[];
  teamList?: Team[];
}

export interface AssigneePickerData {
  notInProjectUsers: User[];
  inProjectUsers: User[];
  teams: Team[];
  companies: Company[];
  limit: number;
  offset: number;
  includedUsers?: User[];
  includedTeams?: Team[];
}

// @TODO this is redundant, we should use the UseAssigneeOptionsProps instead
export interface FetchAssigneePickerDataParams {
  projectId?: number;
  includedUserIds?: number[];
  includedTeamIds?: number[];
  searchString?: string;
  isPrivate?: boolean;
  includeRestricedPartnersMembers?: boolean;
  userType?: UserType[];
  userStatus?: UserStatus[];
  taskStartDateTime?: string; // ISO Date format as string (e.g., "2023-09-01")
  taskEndDateTime?: string; // ISO Date format as string (e.g., "2023-09-13")
  fetchUnResolvedPlaceholdersOnly?: boolean;
  assigneePickerFilter?: AssigneePickerFilterType;
  partners?: UserOption[];
  fieldFilters?: GroupCriteria[];
  startRow?: number;
  endRow?: number;
  includePermissions?: boolean;
  includeTimeSheetApprovers?: boolean;
  includeFields?: boolean;
  groupId?: number;
  companyId?: number;
}

export interface MembersV2Response {
  teams: {
    [key: number]: Team;
  };

  users: {
    [key: number]: User;
  };
}

export interface TeamsResponse {
  [key: number]: Team;
}

export interface TeamWithProjectData extends Team {
  projectToBudgetsMap?: {
    [projectId: number]: number[];
  };
}

export interface TeamsWithMembersResponse {
  teamsMap: {
    [teamId: number]: TeamWithProjectData;
  };
}

export interface UpdateExternalMembers {
  user: {
    userId: number;
  };
  permissionSet: {
    permissionSetId: number;
  };
  projectId: number;
}

export interface BudgetMembership {
  [userOrTeamId: number]: number[];
}

export interface ProjectMembershipData {
  budgetMembership: BudgetMembership;
}

export interface ProjectMembershipMap {
  [projectId: number]: ProjectMembershipData;
}

export interface FinancialBudgetsResponse {
  [memberId: number]: {
    projectMembershipMap: ProjectMembershipMap;
  };
}

export type UpdateMemberPayload = {
  user: Pick<User, 'userId' | 'defaultCapacity' | 'capacityMinutes'>;
  permissionSet?: Pick<PermissionSet, 'permissionSetId'>;
};

export type UpdateMemberPayloadWithFields = {
  user: Pick<User, 'userId' | 'defaultCapacity' | 'capacityMinutes' | 'fields'>;
  permissionSet?: Pick<PermissionSet, 'permissionSetId'>;
};

export type MemberIntegration = {
  userId: number;
  integration: {
    email: {
      emailStatus: string;
    };
  };
};

export type MemberIntegrationsResponse = MemberIntegration[];

export type UpdateMemberIntegrationsPayload = MemberIntegration[];

const assigneePickerFilterToObjectTypeMap = {
  [AssigneePickerFilter.All]: [
    AssigneePickerObjectTypes.InProject,
    AssigneePickerObjectTypes.NotInProject,
    AssigneePickerObjectTypes.Team,
  ],
  [AssigneePickerFilter.NativeUsersAndTeams]: [
    AssigneePickerObjectTypes.InProject,
    AssigneePickerObjectTypes.NotInProject,
    AssigneePickerObjectTypes.Team,
  ],
  [AssigneePickerFilter.NativePartnerUsersAndTeams]: [
    AssigneePickerObjectTypes.InProject,
    AssigneePickerObjectTypes.NotInProject,
    AssigneePickerObjectTypes.Team,
  ],
  [AssigneePickerFilter.UnResolvedPlaceholders]: [
    AssigneePickerObjectTypes.InProject,
    AssigneePickerObjectTypes.NotInProject,
    AssigneePickerObjectTypes.Team,
  ],
  [AssigneePickerFilter.InProjectUsersAndTeams]: [
    AssigneePickerObjectTypes.InProject,
    AssigneePickerObjectTypes.Team,
  ],
  [AssigneePickerFilter.InProjectUsers]: [AssigneePickerObjectTypes.InProject],
  [AssigneePickerFilter.NotInProjectUsers]: [
    AssigneePickerObjectTypes.NotInProject,
  ],
  [AssigneePickerFilter.Teams]: [AssigneePickerObjectTypes.Team],
  [AssigneePickerFilter.NotInProjectUsersAndTeams]: [
    AssigneePickerObjectTypes.NotInProject,
    AssigneePickerObjectTypes.Team,
  ],
  [AssigneePickerFilter.ExternalUsers]: [
    AssigneePickerObjectTypes.InProject,
    AssigneePickerObjectTypes.NotInProject,
  ],
  [AssigneePickerFilter.ExternalAndClientPartnerUsers]: [
    AssigneePickerObjectTypes.InProject,
    AssigneePickerObjectTypes.NotInProject,
  ],
  [AssigneePickerFilter.NativeUsers]: [
    AssigneePickerObjectTypes.InProject,
    AssigneePickerObjectTypes.NotInProject,
  ],
  [AssigneePickerFilter.PlaceholderResolutions]: [
    AssigneePickerObjectTypes.InProject,
    AssigneePickerObjectTypes.NotInProject,
    AssigneePickerObjectTypes.Company,
    AssigneePickerObjectTypes.SameRole,
  ],
};

export class MembersAPIClient extends HttpClient {
  public async findAll(): Promise<MembersResponse> {
    return this.get<MembersResponse>('/members');
  }

  public async getMembersV2(): Promise<MembersV2Response> {
    return this.get<MembersV2Response>('/members/projects');
  }

  public async getTeams(): Promise<TeamsResponse> {
    return this.get<TeamsResponse>('/members/project-teams');
  }

  public async getTeamsWithMembers(): Promise<TeamsWithMembersResponse> {
    return this.get<TeamsWithMembersResponse>('/members/projects/teams');
  }

  public getAllProjectMemberships = async () =>
    this.get<ProjectMembership[]>('/projects/get-members');

  public async inviteMembers(data): Promise<InvitedUser[]> {
    return this.post('/members/bulk-action', data);
  }

  public async updateMember(data: UpdateMemberPayload): Promise<User> {
    return this.put('/members', data);
  }

  public async updateMemberWithFields(
    data: UpdateMemberPayloadWithFields,
  ): Promise<User> {
    return this.put('/members', data);
  }

  public exportAsCSV = async (
    filters: FieldFilter[],
    match: QueryCombinerType,
    userType: UserType = UserType.Native,
    userStatus: UserStatus = UserStatus.Active,
  ): Promise<string> => {
    const { ApiFilter } = await getSimpleSearchFilterModule();

    return this.get<{ downloadUrl: string }>(
      `/members/export?${stringify({ userStatus })}&${stringify({
        userType,
      })}&${ApiFilter.toQueryStringFormat(filters as any)}&${stringify(
        {
          match: filters.length ? match : null,
        },
        { skipNull: true },
      )}`,
    ).then((response) => response.downloadUrl);
  };

  public async updateExternalMembers(
    data: UpdateExternalMembers,
  ): Promise<any> {
    return this.put(`/projects/${data.projectId}/external-members`, {
      user: {
        userId: data.user.userId,
      },
      permissionSet: {
        permissionSetId: data.permissionSet.permissionSetId,
      },
    });
  }

  public fetchPaginatedMembersData = async ({
    groupId,
    projectId = -1,
    searchString = '',
    fieldFilters,
    isPrivate = false,
    includeRestricedPartnersMembers = false,
    taskStartDateTime,
    taskEndDateTime,
    userStatus,
    userType,
    fetchUnResolvedPlaceholdersOnly,
    assigneePickerFilter = 'ALL',
    startRow = 0,
    endRow = 30,
    includePermissions = false,
    includeTimeSheetApprovers = false,
    includeFields = false,
    includedUserIds,
    includedTeamIds,
    companyId,
  }: FetchAssigneePickerDataParams): Promise<AssigneePickerData> => {
    const defaultParams: Record<string, any> = {
      query: searchString,
      offset: startRow,
      limit: endRow - startRow,
    };

    if (includedUserIds?.length) {
      defaultParams.includedUserIds = includedUserIds
        .filter(Number.isFinite)
        .join(',');
    }

    if (includedTeamIds?.length) {
      defaultParams.includedTeamIds = includedTeamIds
        .filter(Number.isFinite)
        .join(',');
    }

    if (fieldFilters?.length) {
      const { ApiGroupFilter } = await getSimpleSearchFilterModule();

      defaultParams.fieldFilters =
        ApiGroupFilter.toQueryStringFormatForGroups(fieldFilters);
    }

    if (fetchUnResolvedPlaceholdersOnly) {
      defaultParams.isResolved = false;
    }

    if (projectId && projectId > 0) {
      defaultParams.projectId = projectId;
    }

    if (isPrivate) {
      defaultParams.isPrivate = true;
    }

    if (includeRestricedPartnersMembers) {
      defaultParams.includeRestricedPartnersMembers = true;
    }

    if (taskStartDateTime && taskEndDateTime) {
      defaultParams.startDateTime = taskStartDateTime;
      defaultParams.endDateTime = taskEndDateTime;
    }

    if (userStatus && userStatus.length > 0) {
      defaultParams.userStatus = userStatus;
    }

    if (userType && userType.length > 0) {
      defaultParams.userType = userType;
    }

    if (groupId) {
      defaultParams.groupId = groupId;
    }

    if (companyId) {
      defaultParams.companyId = companyId;
    }

    // Hadle case where assignee picker filter is set to but user type is not set.
    if (assigneePickerFilter && !userType?.length) {
      if (assigneePickerFilter === 'NATIVE_USERS') {
        defaultParams.userType = [UserType.Native];
      } else if (assigneePickerFilter === 'NATIVE_PARTNER_USERS_AND_TEAMS') {
        defaultParams.userType = [
          UserType.Native,
          UserType.Partner,
          UserType.CustomerPortalBot,
        ];
      } else if (assigneePickerFilter === 'EXTERNAL_USERS') {
        defaultParams.userType = [UserType.External];
      } else if (assigneePickerFilter === 'EXTERNAL_AND_CLIENT_PARTNER_USERS') {
        defaultParams.userType = [UserType.External, UserType.CustomerPartner];
      }
    }

    if (assigneePickerFilter) {
      defaultParams.objectTypes =
        assigneePickerFilterToObjectTypeMap[assigneePickerFilter];
    }

    if (includePermissions) {
      defaultParams.includePermissions = true;
    }

    if (includeTimeSheetApprovers) {
      defaultParams.includeTimeSheetApprovers = true;
    }

    if (includeFields) {
      defaultParams.includeFields = true;
    }

    const url = `/users/search?${stringify(defaultParams)}`;

    const response = await this.get<{
      inProjectList: User[];
      notInProjectList: User[];
      teams: Team[];
      companyList: Company[];
      limit: number;
      offset: number;
      includedUsers?: User[];
      includedTeams?: Team[];
    }>(url);

    const isRealUser = (u: User) => u.emailId?.includes('@');

    return {
      inProjectUsers: (response?.inProjectList ?? []).filter(isRealUser),
      notInProjectUsers: (response?.notInProjectList ?? []).filter(isRealUser),
      teams: uniqBy(response?.teams ?? [], 'teamId'),
      companies: response?.companyList ?? [],
      limit: response?.limit ?? 0,
      offset: response?.offset ?? 0,
      includedUsers: (response?.includedUsers ?? []).filter(isRealUser),
      includedTeams: uniqBy(response?.includedTeams ?? [], 'teamId'),
    };
  };

  public fetchUnresolvedPlaceholders = async ({
    projectId,
    searchString = '',
    fetchUnResolvedPlaceholdersOnly = false,
    assigneePickerFilter,
  }: FetchAssigneePickerDataParams): Promise<AssigneePickerData> => {
    const defaultParams: Record<string, any> = {
      offset: 0,
      limit: 30,
      isResolved: fetchUnResolvedPlaceholdersOnly ? false : undefined,
    };

    if (searchString) {
      defaultParams.query = searchString;
    }

    if (projectId && projectId > 0) {
      defaultParams.projectId = projectId;
    }

    if (assigneePickerFilter) {
      defaultParams.objectTypes =
        assigneePickerFilterToObjectTypeMap[assigneePickerFilter];
    }

    const url = `/teams/search?${stringify(defaultParams)}`;

    const response = await this.get<{
      teams: Team[];
      notInProjectUsers: User[];
      limit: number;
      offset: number;
    }>(url);

    return {
      inProjectUsers: [],
      notInProjectUsers: [],
      teams: response?.teams ?? [],
      companies: [],
      limit: response?.limit ?? 0,
      offset: response?.offset ?? 0,
    };
  };

  public exportSkillsAsCSV = async ({
    filters,
    match,
    columnOrder,
  }): Promise<ExportMetaData> => {
    const { ApiGroupFilter } = await getSimpleSearchFilterModule();
    const url = `/members/skills/export?${ApiGroupFilter.toQueryStringFormatForGroups(
      filters,
    )}&${stringify(
      { match: filters.length ? match : null },
      { skipNull: true },
    )}`;

    return this.post<ExportMetaData, { columnOrder: string[] }>(url, {
      columnOrder,
    });
  };

  public getProjectsFinancialBudgets = async (
    projectIds?: number[],
  ): Promise<FinancialBudgetsResponse> => {
    const url = projectIds?.length
      ? `/members/projects/financial-budgets?projectIds=${projectIds.join(',')}`
      : '/members/projects/financial-budgets';

    return this.get<FinancialBudgetsResponse>(url);
  };

  public async getMemberIntegrations(): Promise<MemberIntegrationsResponse> {
    return this.get('/members/integrations');
  }

  public async updateMemberIntegrations(
    data: UpdateMemberIntegrationsPayload,
  ): Promise<User[]> {
    return this.put('/members/integrations', data);
  }
}

export const MembersAPI: MembersAPIClient = process.env.VITEST
  ? ((globalThis as any)[Symbol.for('rocketlane:MembersAPI')] ??=
      new MembersAPIClient())
  : new MembersAPIClient();

export * from './types';
